import intersection from 'helpers/walls/intersection'
import buildPolygon from 'helpers/walls/buildPolygon'
import { findThroughIntersections } from 'helpers/walls/findIntersections'
import projectRadius, { projectMiddle } from 'helpers/walls/projectRadius'
import lineIntersectsCircle from 'helpers/walls/lineIntersectsCircle'
import notifyHoneybadger from 'helpers/notifyHoneybadger'
import polygonClipping from 'polygon-clipping'
import rewind from 'geojson-rewind'

/**
 * Rounds the provided number to limit precision
 *
 * @param {Number} n - The number to be rounded
 * @param {Number} base - The rounding factor
 * @return {Number} The rounded number
 */
const round = (n, base) => Math.round((n + Number.EPSILON) * base) / base

// bases to try for rounding
const roundingBases = [10000000, 50000000, 100000000]

/**
 * Transforms an array of google.maps.Point objects into polygon coordinates,
 * according to GeoJSON standard
 *
 * @param {Array} arr - The Point objects to transform
 * @param {Number} base - The rounding base
 * @return {Array} The rounded coordinates
 */
const getCoords = (arr, base = roundingBases[0]) => {
  let coords = []
  arr.forEach(p => {
    let point = []
    point[0] = round(p.x, base)
    point[1] = round(p.y, base)
    coords.push(point)
  })
  return rewind({ type: 'Polygon', coordinates: [coords] }, false).coordinates
}

/**
 * Transforms an array of rings of google.maps.Point objects into polygon coordinates,
 * according to GeoJSON standard
 *
 * @param {Array} arr - The rings containing Point objects to transform
 * @param {Number} base - The rounding base
 * @return {Array} The rounded coordinates
 */
const getRingCoords = (arr, base = roundingBases[0]) => {
  let coords = []
  arr.forEach(ring => {
    let ringCoords = []
    ring.forEach(p => {
      let point = []
      point[0] = round(p.x, base)
      point[1] = round(p.y, base)
      ringCoords.push(point)
    })
    coords.push(ringCoords)
  })
  return rewind({ type: 'Polygon', coordinates: coords }, false).coordinates
}

/**
 * Transforms an array of polygon coordinates into google.maps.LatLng objects,
 * based on provided projection
 *
 * @param {Array} arr - The polygon coordinates to transform
 * @param {google.maps.Projection} projection - The map projection to use to convert to LatLng objects
 * @return {Array} The rounded coordinates
 */
const toCoords = (arr, projection) => {
  if (arr.length === 0) return []

  // map each polygon and each ring, flatten into single polygon with many rings,
  // let Google remove areas based on ring winding order
  return arr.map(poly => poly.map(ring => ring.map(c => projection.fromPointToLatLng(new google.maps.Point(c[0], c[1]))))).flat(1)
}

/**
 * Attempts to combine an array of polygon coordinates into a single polygon
 * and remove the result from the provided polygon, with retries
 *
 * @param {Array} intersectingPolygons - The polygons to remove
 * @param {Array} initialCoords - The original polygon
 * @param {Array} initialPath - Array of google.maps.LatLng objects representing the unconverted polygon
 * @param {Number} retry - The current retry
 * @return {Array} The resulting polygon coordinates
 */
const tryClippingView = (intersectingPolygons, initialCoords, initialPath, retry = 0) => {
  try {
    const roundedCoords = getCoords(initialPath, roundingBases[retry])
    const intersectionCoords = polygonClipping.union(...intersectingPolygons.map(poly => getCoords(poly, roundingBases[retry])))
    return polygonClipping.difference(roundedCoords, intersectionCoords)
  } catch (err) {
    if (retry < roundingBases.length - 1) {
      return tryClippingView(intersectingPolygons, initialCoords, initialPath, retry + 1)
    }

    // notify HB and return initial coordinates as fallback
    notifyHoneybadger({ err, action: { type: 'WALL_INTERSECTION', skipReport: true } })
    return initialCoords
  }
}

/**
 * Computes the intersection of the provided path and restricted FoV
 *
 * @param {Array} path - The polygon path to intersect, as an array of google.maps.LatLng objects
 * @param {Array} clippedView - The restricted FoV as a GeoJSON polygon
 * @param {google.maps.Map} map - The map to use to convert to LatLng objects
 * @return {Array} The intersection, as an array of google.maps.LatLng objects
 */

export function computeIntersection(path, clippedView, map) {
  if (!clippedView) return path
  const projection = map.getProjection()
  if (!projection) return path

  for (let i = 0; i < roundingBases.length; i++) {
    try {
      const newPath = polygonClipping.intersection(clippedView, getCoords(path.map(l => projection.fromLatLngToPoint(l)), roundingBases[i]))
      return toCoords(newPath, projection)
    } catch (err) {
      if (i === roundingBases.length - 1) {
        notifyHoneybadger({ err, action: { type: 'WALL_COMPUTE_INTERSECTION', skipReport: true } })
      }
    }
  }

  return path
}

/**
 * Computes the intersection of the provided ring paths and restricted FoV
 *
 * @param {Array} paths - The polygon path to intersect, as an array of array of google.maps.LatLng objects
 * @param {Array} clippedView - The restricted FoV as a GeoJSON polygon
 * @param {google.maps.Map} map - The map to use to convert to LatLng objects
 * @return {Array} The intersection, as an array of array of google.maps.LatLng objects
 */

export function computeRingIntersection(paths, clippedView, map) {
  if (!clippedView) return paths
  const projection = map.getProjection()
  if (!projection) return paths

  for (let i = 0; i < roundingBases.length; i++) {
    try {
      const ringCoords = getRingCoords(paths.map(ring => ring.map(l => projection.fromLatLngToPoint(l))), roundingBases[i])
      const newPath = polygonClipping.intersection(clippedView, ringCoords)
      return toCoords(newPath, projection)
    } catch (err) {
      if (i === roundingBases.length - 1) {
        notifyHoneybadger({ err, action: { type: 'WALL_COMPUTE_RING_INTERSECTION', skipReport: true } })
      }
    }
  }

  return paths
}

/**
 * Removes all wall blindspots from a given camera's FoV
 *
 * @param {Object} args - Object to be deconstructed which contains arguments
 * @param {Array} args.walls - All wall objects
 * @param {Number} args.angle - Camera angle
 * @param {Number} args.distance - Camera distance
 * @param {Number} args.heading - Camera heading
 * @param {google.maps.LatLng} args.cameraMarkerPosition - Camera marker position
 * @param {google.maps.Map} args.map - The map to use to convert to LatLng objects
 * @return {Array} The restricted FoV, as a GeoJSON polygon
 */

export default function calculateClippedView({ walls, angle, distance, heading, cameraMarkerPosition, map }) {
  const projection = map.getProjection()
  if (!projection) return

  const leftPosition = google.maps.geometry.spherical.computeOffset(cameraMarkerPosition, distance / Math.cos(Math.PI * (angle / 2) / 180), heading - (angle / 2))
  const rightPosition = google.maps.geometry.spherical.computeOffset(cameraMarkerPosition, distance / Math.cos(Math.PI * (angle / 2) / 180), heading + (angle / 2))
  const triangle = new google.maps.Polygon({
    paths: [cameraMarkerPosition, leftPosition, rightPosition, cameraMarkerPosition],
    visible: false,
    map: map
  })

  let intersectingPolygons = []
  const leftPoint = projection.fromLatLngToPoint(leftPosition)
  const rightPoint = projection.fromLatLngToPoint(rightPosition)
  const cameraPoint = projection.fromLatLngToPoint(cameraMarkerPosition)

  walls.forEach(wall => {
    const path = wall.overlay && wall.overlay.getPath().getArray() || []
    for (let i = 0; i < path.length - 1; i++) {
      const uIn = google.maps.geometry.poly.containsLocation(path[i], triangle)
      const vIn = google.maps.geometry.poly.containsLocation(path[i + 1], triangle)
      const uPoint = projection.fromLatLngToPoint(path[i])
      const vPoint = projection.fromLatLngToPoint(path[i + 1])

      if (uIn && vIn) {
        const uProj = intersection(cameraPoint, uPoint, leftPoint, rightPoint)
        const vProj = intersection(cameraPoint, vPoint, leftPoint, rightPoint)

        intersectingPolygons.push([uPoint, uProj, vProj, vPoint, uPoint])
      } else if (uIn || vIn) {
        const innerPoint = uIn ? uPoint : vPoint
        const outerPoint = uIn ? vPoint : uPoint
        const proj = intersection(cameraPoint, innerPoint, leftPoint, rightPoint)

        intersectingPolygons.push(buildPolygon({ innerPoint, proj, outerPoint, cameraPoint, leftPoint, rightPoint }))
      } else {
        const { left, right, end } = findThroughIntersections({ innerPoint: projection.fromLatLngToPoint(path[i]), outerPoint: projection.fromLatLngToPoint(path[i + 1]), cameraPoint, leftPoint, rightPoint, through: true })

        if (left && right) {
          intersectingPolygons.push([leftPoint, rightPoint, right, left, leftPoint])
        } else if (left && end) {
          intersectingPolygons.push([leftPoint, left, end, leftPoint])
        } else if (right && end) {
          intersectingPolygons.push([rightPoint, end, right, rightPoint])
        }
      }
    }
  })

  const initialCoords = getCoords([cameraPoint, rightPoint, leftPoint, cameraPoint])
  let triangleCoords = initialCoords

  if (intersectingPolygons.length > 0) {
    triangleCoords = tryClippingView(intersectingPolygons, initialCoords, [cameraPoint, rightPoint, leftPoint, cameraPoint])
  }

  triangle.setMap(null)

  return triangleCoords
}


/**
 * Removes all wall blindspots from a 360 camera's FoV
 *
 * @param {Object} args - Object to be deconstructed which contains arguments
 * @param {Array} args.walls - All wall objects
 * @param {Array} args.path - The camera FoV path
 * @param {Number} args.distance - Camera distance
 * @param {google.maps.LatLng} args.cameraMarkerPosition - Camera marker position
 * @param {google.maps.LatLng} args.subjectMarkerPosition - Subject marker position
 * @param {google.maps.Map} args.map - The map to use to convert to LatLng objects
 * @return {Array} The restricted FoV, as a GeoJSON polygon
 */

export function calculateClippedCircle({ walls, path, distance, cameraMarkerPosition, subjectMarkerPosition, map }) {
  const projection = map.getProjection()
  if (!projection) return

  const circle = new google.maps.Polygon({
    paths: path,
    visible: false,
    map: map
  })

  let intersectingPolygons = []

  walls.forEach(wall => {
    const path = wall.overlay && wall.overlay.getPath().getArray() || []
    for (let i = 0; i < path.length - 1; i++) {
      const uIn = google.maps.geometry.poly.containsLocation(path[i], circle)
      const vIn = google.maps.geometry.poly.containsLocation(path[i + 1], circle)
      const uPoint = projection.fromLatLngToPoint(path[i])
      const vPoint = projection.fromLatLngToPoint(path[i + 1])

      if (uIn && vIn) {
        const uProj = projectRadius(cameraMarkerPosition, distance, path[i], projection)
        const vProj = projectRadius(cameraMarkerPosition, distance, path[i + 1], projection)
        const uvProj = projectMiddle(cameraMarkerPosition, distance, path[i], path[i + 1], projection)

        intersectingPolygons.push([uPoint, uProj, uvProj, vProj, vPoint, uPoint])
      } else if (uIn || vIn) {
        const innerPoint = uIn ? uPoint : vPoint
        const outerPoint = uIn ? vPoint : uPoint
        const innerProj = projectRadius(cameraMarkerPosition, distance, uIn ? path[i] : path[i + 1], projection)
        const midProj = projectMiddle(cameraMarkerPosition, distance, path[i], path[i + 1], projection)
        const outerProj = projectRadius(cameraMarkerPosition, distance, uIn ? path[i + 1] : path[i], projection)

        intersectingPolygons.push([outerPoint, innerPoint, innerProj, midProj, outerProj, outerPoint])
      } else {
        const cameraPoint = projection.fromLatLngToPoint(cameraMarkerPosition)
        const subjectPoint = projection.fromLatLngToPoint(subjectMarkerPosition)

        if (lineIntersectsCircle(uPoint, vPoint, cameraPoint, subjectPoint)) {
          const uProj = projectRadius(cameraMarkerPosition, distance, path[i], projection)
          const vProj = projectRadius(cameraMarkerPosition, distance, path[i + 1], projection)
          const uvProj = projectMiddle(cameraMarkerPosition, distance, path[i], path[i + 1], projection)

          intersectingPolygons.push([uPoint, uProj, uvProj, vProj, vPoint, uPoint])
        }
      }
    }
  })

  const initialCoords = getCoords(path.map(c => projection.fromLatLngToPoint(c)))
  let circleCoords = initialCoords

  if (intersectingPolygons.length > 0) {
    circleCoords = tryClippingView(intersectingPolygons, initialCoords, path.map(c => projection.fromLatLngToPoint(c)))
  }

  circle.setMap(null)

  return circleCoords
}



// WEBPACK FOOTER //
// ./src/components/Camera/helpers/calculateClippedView.js